import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

type Props = {
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  length?: number;
  /** Marca los casilleros como inválidos tras un intento fallido. */
  invalido?: boolean;
  /** Id del texto de ayuda asociado (aria-describedby). */
  ayudaId?: string;
};

export function OtpBoxes({
  value,
  onChange,
  disabled,
  length = 6,
  invalido = false,
  ayudaId,
}: Props) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');
  const completo = value.replace(/\D/g, '').length === length;

  useEffect(() => {
    if (disabled) return;
    const vacio = digits.findIndex((d) => !d);
    refs.current[vacio === -1 ? length - 1 : vacio]?.focus();
    // Solo al montar o al habilitarse: no robar el foco en cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const setAt = (index: number, char: string) => {
    const next = digits.map((d, i) => (i === index ? char : d));
    onChange(next.join('').replace(/\D/g, '').slice(0, length));
  };

  const onKey = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[index]) {
        setAt(index, '');
      } else if (index > 0) {
        setAt(index - 1, '');
        refs.current[index - 1]?.focus();
      }
      return;
    }
    if (e.key === 'Delete') {
      e.preventDefault();
      setAt(index, '');
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    onChange(text);
    const focusIdx = Math.min(text.length, length - 1);
    refs.current[focusIdx]?.focus();
  };

  return (
    <>
      <div
        className={`otp${invalido ? ' otp--error' : ''}`}
        role="group"
        aria-label={`Código de verificación de ${length} dígitos`}
        aria-describedby={ayudaId}
      >
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={`otp__box${d ? ' otp__box--lleno' : ''}${invalido ? ' otp__box--error' : ''}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={d}
            disabled={disabled}
            aria-label={`Dígito ${i + 1} de ${length}`}
            aria-invalid={invalido || undefined}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => {
              const char = e.target.value.replace(/\D/g, '').slice(-1);
              setAt(i, char);
              if (char && i < length - 1) refs.current[i + 1]?.focus();
            }}
            onKeyDown={(e) => onKey(i, e)}
            onPaste={onPaste}
          />
        ))}
      </div>
      <p className="otp__estado ayuda" role="status">
        {completo ? 'Código completo. Puede verificar.' : `Ingrese los ${length} dígitos.`}
      </p>
    </>
  );
}
